import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import RadioButtonInput from '../components/Inputs/RadioButtonInput/RadioButtonInput';
import TextInput from '../components/Inputs/TextInput/TextInput';
import useListOfForms from './useListOfForms';
import { selectAppConfig } from '../redux/appConfigReducer';
import { selectInputs } from '../redux/inputsReducer';
import DropDownSelector from '../components/Inputs/DropdownInput/DropDownSelector';
import VehicleDropdown from '../components/Inputs/DropdownInput/vehicleDropDown';
// import { TOPIC_OF_INTEREST } from '../constants';
import useInputOptions from './useInputOptions';
import { INPUT_TYPE } from '../constants';
import { modelFamiliesOptions, modelLinesByFamilyOptions } from '../utils/vehicleFormattor';
import useSourceOfLead from './useSourceOfLead';

// Should be the main hook that will allow to build components.
// We should import here all the JSX individual components
// And write logic where based on the type of form - we are returning back prepared and ready to use lead form
// First we will call useListOfForms, and pass there name of the requested form.
// Then we will get back array with components that should render. Based on that array we will map and render
// Components we want to see on the page, and we will send it back to the main DigitalLeads.tsx

type LeadInput = {
  inputKey: string;
  Component: JSX.Element;
};

const isAlpha = !process.env.IS_LEGACY;

const useLeadsBuilder = () => {
  // Grab LF name from Redux
  // Pass it to useListForms and get back list of components need to render
  const { t } = useTranslation();
  const {
    TOPIC_OF_INTEREST,
    PREFERRED_CONTACT_METHOD,
    TIER3_TOPIC_OF_INTEREST,
    ALL_TOPIC_OF_INTEREST,
    CONTACT_TIME,
  } = useInputOptions();

  const { presetForm, customForm, vehicleId, market, topicOfInterests } =
    useSelector(selectAppConfig);

  const { modelFamilyOfInterest: modelFamilyOfInterestSelection, preferredContactMethod } =
    useSelector(selectInputs);

  const isUSMarket = market?.toLowerCase().includes('us');

  const { generalSource } = useSourceOfLead(isUSMarket);

  const hasOptionalField = presetForm === 'tradeIn';

  const listOfInputs = useListOfForms(presetForm);

  const isTier1 = generalSource === 'tier_1';

  const topicOfInterestListToRender = useMemo(() => {
    if (topicOfInterests.length)
      return Object.fromEntries(
        Object.entries(ALL_TOPIC_OF_INTEREST).filter(([key]) => topicOfInterests.includes(key)),
      );
    if (isTier1) return TOPIC_OF_INTEREST;
    return TIER3_TOPIC_OF_INTEREST;
  }, [
    ALL_TOPIC_OF_INTEREST,
    TIER3_TOPIC_OF_INTEREST,
    TOPIC_OF_INTEREST,
    isTier1,
    topicOfInterests,
  ]);

  const topicOfInterest = useMemo(() => {
    const label = isAlpha
      ? t('digitalLeads.inputs.topicOfInterest.placeHolder')
      : `${t('digitalLeads.inputs.topicOfInterest.placeHolder')} *`;

    return {
      inputKey: INPUT_TYPE.TOPIC_OF_INTEREST,
      Component: (
        <DropDownSelector
          options={topicOfInterestListToRender}
          title={t('digitalLeads.inputs.topicOfInterest.title')}
          inputLabel={label}
          inputKey={INPUT_TYPE.TOPIC_OF_INTEREST}
          dropdownHeight={generalSource === 'tier_1' ? 260 : 392}
        />
      ),
    };
  }, [topicOfInterestListToRender, t, generalSource]);

  const firstNameInput = useMemo(() => {
    let label = t('digitalLeads.inputs.firstNamePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.FIRST_NAME,
      Component: <TextInput inputLabel={label} inputKey={INPUT_TYPE.FIRST_NAME} inputType="text" />,
    };
  }, [hasOptionalField, t]);

  const lastNameInput = useMemo(() => {
    let label = t('digitalLeads.inputs.lastNamePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }
    return {
      inputKey: INPUT_TYPE.LAST_NAME,
      Component: <TextInput inputLabel={label} inputKey={INPUT_TYPE.LAST_NAME} inputType="text" />,
    };
  }, [hasOptionalField, t]);

  const zipInput = useMemo(() => {
    let label = isUSMarket
      ? t('digitalLeads.inputs.zipCodePlaceHolder')
      : t('digitalLeads.inputs.postalCodePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.ZIP,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.ZIP}
          inputType="text"
          inputNote={
            isUSMarket ? t('digitalLeads.inputs.zipNote') : t('digitalLeads.inputs.postalNote')
          }
        />
      ),
    };
  }, [hasOptionalField, isUSMarket, t]);

  const emailInput = useMemo(() => {
    let label = t('digitalLeads.inputs.emailPlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.EMAIL,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.EMAIL}
          inputType="email"
          inputNote={t('digitalLeads.inputs.emailNote')}
        />
      ),
    };
  }, [hasOptionalField, t]);

  const phoneNumberInput = useMemo(() => {
    const isOptional = preferredContactMethod === INPUT_TYPE.EMAIL;
    const label = isOptional || isAlpha ? 'Enter your phone number' : 'Enter your phone number *';
    return {
      inputKey: INPUT_TYPE.PHONE,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.PHONE}
          inputType="text"
          isOptional={isOptional}
        />
      ),
    };
  }, [preferredContactMethod]);

  const preferedContact = useMemo(() => {
    let title = t('digitalLeads.inputs.preferredContactMethod.label');
    if (!hasOptionalField && !isAlpha) {
      title = `${title} *`;
    }

    return {
      inputKey: INPUT_TYPE.PREFERED_METHOD,
      Component: (
        <RadioButtonInput
          title={title}
          inputKey={INPUT_TYPE.PREFERED_METHOD}
          options={PREFERRED_CONTACT_METHOD}
        />
      ),
    };
  }, [PREFERRED_CONTACT_METHOD, hasOptionalField, t]);

  const preferedContactTime = useMemo(() => {
    const isPhoneInput = preferredContactMethod !== INPUT_TYPE.EMAIL;
    if (!isPhoneInput) return null;
    return {
      inputKey: INPUT_TYPE.CONTACT_TIME,
      Component: (
        <DropDownSelector
          options={CONTACT_TIME}
          inputLabel="Preferred contact time"
          inputKey={INPUT_TYPE.CONTACT_TIME}
          inputNote="While your dealer will do their best to accommodate, your chosen time is not guaranteed."
          isOptional
          dropdownHeight={176}
        />
      ),
    };
  }, [CONTACT_TIME, preferredContactMethod]);

  const modelFamilyOfInterest = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.MODEl_FAMILY_OF_INTEREST,
      Component: (
        <VehicleDropdown
          options={modelFamiliesOptions}
          inputLabel={`${t('digitalLeads.inputs.modelFamilyOfInterestPlaceHolder')}`}
          inputKey={INPUT_TYPE.MODEl_FAMILY_OF_INTEREST}
          dropdownHeight={350}
        />
      ),
    };
  }, [t]);

  const modelLineOfInterest = useMemo(() => {
    const options = modelLinesByFamilyOptions[modelFamilyOfInterestSelection] || [];

    return {
      inputKey: INPUT_TYPE.MODEl_LINE_OF_INTEREST,
      Component: (
        <VehicleDropdown
          options={options}
          inputLabel={t('digitalLeads.inputs.modelLineOfInterestPlaceHolder')}
          inputKey={INPUT_TYPE.MODEl_LINE_OF_INTEREST}
          isOptional
          dropdownHeight={350}
        />
      ),
    };
  }, [modelFamilyOfInterestSelection, t]);

  const tradeInVehicleYear = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_YEAR,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleYear')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_YEAR}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleMake = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MAKE,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleMake')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MAKE}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleModel = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MODEL,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleModel')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MODEL}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleTrimLine = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_TRIM_LINE,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleTrimLine')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_TRIM_LINE}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleMileage = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleMileage')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleVin = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_VIN,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleVin')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_VIN}
          inputType="text"
          inputNote={t('digitalLeads.inputs.vinNote')}
          helperIcon
          isOptional
          popoverDescription={t('digitalLeads.vinPopover.description')}
          popoverTitle={t('digitalLeads.vinPopover.title')}
        />
      ),
    };
  }, [t]);

  // If we are adding new JSX component as a variable here - need to add this var in the array below

  const isLeadInput = (x: LeadInput | null | undefined): x is LeadInput => !!x;

  const fullListOfAllInputs = useMemo(() => {
    return [
      firstNameInput,
      lastNameInput,
      zipInput,
      preferedContact,
      emailInput,
      phoneNumberInput,
      preferedContactTime,
    ].filter(isLeadInput);
  }, [
    emailInput,
    firstNameInput,
    lastNameInput,
    preferedContact,
    phoneNumberInput,
    zipInput,
    preferedContactTime,
  ]);

  const topicOfInterestInput = useMemo(() => {
    if (
      customForm?.includes(INPUT_TYPE.TOPIC_OF_INTEREST) ||
      listOfInputs?.includes(INPUT_TYPE.TOPIC_OF_INTEREST)
    ) {
      return [topicOfInterest];
    }
    return null;
  }, [customForm, listOfInputs, topicOfInterest]);

  const tradeInVehicleInputs = useMemo(() => {
    if (presetForm === 'tradeIn') {
      return [
        tradeInVehicleYear,
        tradeInVehicleMake,
        tradeInVehicleModel,
        tradeInVehicleTrimLine,
        tradeInVehicleMileage,
        tradeInVehicleVin,
      ];
    }
    return null;
  }, [
    presetForm,
    tradeInVehicleMake,
    tradeInVehicleMileage,
    tradeInVehicleModel,
    tradeInVehicleTrimLine,
    tradeInVehicleVin,
    tradeInVehicleYear,
  ]);

  const modelSelections = useMemo(() => {
    if (presetForm === 'tradeIn' && !vehicleId) {
      return [modelFamilyOfInterest, modelLineOfInterest];
    }
    return null;
  }, [modelFamilyOfInterest, modelLineOfInterest, presetForm, vehicleId]);

  const buildedForm = useMemo(() => {
    if (customForm?.length)
      return fullListOfAllInputs.filter((input) => customForm.includes(input.inputKey));
    if (presetForm) {
      return fullListOfAllInputs.filter((input) => listOfInputs?.includes(input.inputKey));
    }
    return fullListOfAllInputs.filter((input) => listOfInputs?.includes(input.inputKey));
  }, [customForm, fullListOfAllInputs, listOfInputs, presetForm]);

  return { topicOfInterestInput, tradeInVehicleInputs, modelSelections, buildedForm };
};

export default useLeadsBuilder;
